import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { useEffect, useSyncExternalStore } from 'react';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/cn';

type Tone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: Tone;
  /** Text, or an error the Toaster translates (so toasts follow the language). */
  content: string | unknown;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function push(tone: Tone, content: unknown): number {
  const id = nextId++;
  items = [...items.slice(-3), { id, tone, content }];
  emit();
  return id;
}

export function dismissToast(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

export const toast = {
  success: (text: string) => push('success', text),
  info: (text: string) => push('info', text),
  /** Accepts an ApiError (translated by code) or a ready message. */
  error: (error: unknown) => push('error', error),
};

const ICONS = { success: CircleCheck, error: CircleAlert, info: Info } as const;
const TONES: Record<Tone, string> = {
  success: 'bg-ink text-white',
  info: 'bg-ink text-white',
  error: 'bg-danger-tint text-danger-text border border-danger/30',
};

function ToastView({ item }: { item: ToastItem }) {
  const { errorText, t } = useI18n();
  const Icon = ICONS[item.tone];
  const text = typeof item.content === 'string' ? item.content : errorText(item.content);
  useEffect(() => {
    const id = setTimeout(() => dismissToast(item.id), item.tone === 'error' ? 8000 : 4000);
    return () => clearTimeout(id);
  }, [item]);
  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn('pointer-events-auto flex max-w-md items-start gap-3 rounded-lg px-4 py-3 text-base shadow-pop animate-toast-in', TONES[item.tone])}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-5 shrink-0', item.tone === 'success' && 'text-accent-soft')} />
      <span className="min-w-0 flex-1">{text}</span>
      <button
        type="button"
        onClick={() => dismissToast(item.id)}
        aria-label={t('common.close')}
        className="-m-2 grid size-9 shrink-0 place-items-center rounded-md opacity-80 hover:opacity-100"
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  const list = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => items,
  );
  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-center gap-2 print:hidden">
      {list.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}
